import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { roundRobinApi } from '@/lib/api/services/roundRobin';
import {
  Users,
  Search,
  Edit,
  Clock,
  AlertCircle,
  Key,
  MessageCircle,
  ShieldAlert,
  ShieldCheck,
  Lock,
  MoreHorizontal,
} from 'lucide-react';
import { ChatWindow } from '@/components/chat/ChatWindow';
import { DirectMessageWindow } from '@/components/chat/DirectMessageWindow';
import { UserSearchModal } from '@/components/chat/UserSearchModal';
import { TeamMessagingSidebar } from '@/components/teams/TeamMessagingSidebar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MinimalInviteDialog } from '@/components/teams/MinimalInviteDialog';
import { MinimalEditDialog } from '@/components/teams/MinimalEditDialog';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { MemberPermissionsDialog } from './MemberPermissionsDialog';
import {
  DotStatusPill,
  SoftRolePill,
  TeamManagementShell,
  TeamMembersFilterBar,
  TeamRoundRobinFilterBar,
  formatBusinessAccessLabel,
  teamMemberInitials,
  teamMemberStatusKey,
  teamRolePill,
  teamRolePillFromRole,
  teamStatusPill,
  type TeamManagementTab,
} from '@/components/teams/teamManagementChrome';
import { TeamService, TeamMember, TeamStats } from '@/lib/api/services/teamService';
import { useToast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors/showApiError';
import { useAuth } from '@/components/AuthProvider';
import { useBusinessAccounts } from '@/hooks/useBusinessAccounts';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { useChat } from '@/contexts/ChatContext';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { cn } from '@/lib/utils';

const thClass =
  'text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[#98A2B3] px-3.5 py-3 whitespace-nowrap bg-[#FAFAFB] text-left';
const tdClass = 'px-3.5 py-3.5 align-middle';

const TeamMembersTableSkeleton = () => (
  <div className="px-[15px] py-4">
    <div className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white">
      <div className="space-y-3 p-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="ml-auto h-4 w-40" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const MinimalTeamManagement: React.FC = () => {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParam = searchParams.get('search') || '';
  const [searchTerm, setSearchTerm] = useState(searchParam);

  useEffect(() => {
    setSearchTerm(searchParam);
  }, [searchParam]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== searchParam) {
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          if (searchTerm) next.set('search', searchTerm);
          else next.delete('search');
          return next;
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchParam, setSearchParams]);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<TeamMember | null>(null);

  const [passwordChangeUser, setPasswordChangeUser] = useState<TeamMember | null>(null);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState<TeamMember | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamStats, setTeamStats] = useState<TeamStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { data: businessAccounts } = useBusinessAccounts();
  const { data: entitlements } = useOrganizationEntitlements();
  const chatContext = useChat();
  const orgTeamRoomId = currentUser?.organization_id ? `org_${currentUser.organization_id}_team_general` : 'team-general';

  const validTabs: TeamManagementTab[] = ['members', 'messaging', 'round-robin'];
  const tabParam = searchParams.get('tab') || 'members';
  const activeManagementTab: TeamManagementTab = validTabs.includes(tabParam as TeamManagementTab)
    ? (tabParam as TeamManagementTab)
    : 'members';
  const setActiveManagementTab = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [messagingTab, setMessagingTab] = useState<'team' | 'chat'>('team');
  const [messagingSelectedMember, setMessagingSelectedMember] = useState<string | null>(null);

  const parseAccessFlag = (value: unknown): boolean => {
    return value === true || value === 1 || value === '1' || value === 'true';
  };
  const [messagingSelectedMemberName, setMessagingSelectedMemberName] = useState<string>('');
  const [isUserSearchOpen, setIsUserSearchOpen] = useState(false);

  const handleMessagingMemberSelect = (memberId: string) => {
    const member = chatContext?.teamMembers?.find(m => m.id === memberId);
    setMessagingSelectedMember(memberId);
    setMessagingSelectedMemberName(member?.full_name || member?.email || 'Unknown');
    setMessagingTab('chat');
    chatContext?.markAsRead?.(memberId);
  };

  const handleMessagingSelectTeam = () => {
    setMessagingTab('team');
    setMessagingSelectedMember(null);
    setMessagingSelectedMemberName('');
    chatContext?.markAsRead?.(orgTeamRoomId);
  };

  const handleMessagingStartDM = (userId: string, userName: string) => {
    setMessagingSelectedMember(userId);
    setMessagingSelectedMemberName(userName);
    setMessagingTab('chat');
    setIsUserSearchOpen(false);
  };

  useEffect(() => {
    if (!chatContext) return;
    if (!chatContext.isConnected && !chatContext.isConnecting) {
      chatContext.connect();
    }
  }, [chatContext]);

  useEffect(() => {
    if (!chatContext?.isConnected || !chatContext.socket || !orgTeamRoomId) return;
    chatContext.socket.emit('join_room', { roomId: orgTeamRoomId });
  }, [chatContext?.isConnected, chatContext?.socket, orgTeamRoomId]);

  const teamUnread = chatContext?.unreadCounts?.[orgTeamRoomId] || 0;
  useEffect(() => {
    if (messagingTab === 'team' && teamUnread > 0) {
      chatContext?.markAsRead(orgTeamRoomId);
    }
  }, [messagingTab, teamUnread, chatContext, orgTeamRoomId]);

  useEffect(() => {
    if (!chatContext?.socket) return;

    const handleUserStatus = (data: { userId: string; is_online: boolean; last_seen: string }) => {
      setTeamMembers((prev) =>
        prev.map((member) =>
          member.id === data.userId
            ? { ...member, is_online: data.is_online, last_seen: data.last_seen }
            : member
        )
      );
    };

    chatContext.socket.on('user_status', handleUserStatus);

    return () => {
      chatContext.socket?.off('user_status', handleUserStatus);
    };
  }, [chatContext?.socket]);

  const presenceMap = useMemo(() => {
    if (!chatContext?.teamMembers || chatContext.teamMembers.length === 0) return null;

    return chatContext.teamMembers.reduce<Record<string, { is_online?: boolean; last_seen?: string }>>((acc, member) => {
      acc[member.id] = {
        is_online: member.is_online,
        last_seen: member.last_seen,
      };
      return acc;
    }, {});
  }, [chatContext?.teamMembers]);

  const membersWithPresence = useMemo(() => {
    if (!presenceMap) return teamMembers;

    return teamMembers.map((member) => {
      const presence = presenceMap[member.id];
      if (!presence) return member;

      return {
        ...member,
        is_online: presence.is_online ?? member.is_online,
        last_seen: presence.last_seen ?? member.last_seen,
      };
    });
  }, [teamMembers, presenceMap]);

  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [membersResponse, statsResponse] = await Promise.all([
        TeamService.getTeamMembers(),
        TeamService.getTeamStats()
      ]);


      if ((membersResponse as any)?.success) {
        setTeamMembers(((membersResponse as any).data) as any);
      } else {
      }

      if ((statsResponse as any)?.success) {
        setTeamStats(((statsResponse as any).data) as any);
      } else {
      }
    } catch (err) {
      setError('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const roleRaw = (currentUser?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!(currentUser as any)?.is_super_admin;
  const isAdmin = roleRaw === 'ADMIN' || isSuperAdmin;

  const getInvitationStatus = (member: TeamMember) => {
    const now = new Date();
    const createdAt = new Date(member.created_at || 0);
    const lastLogin = member.last_login
      ? new Date(member.last_login)
      : (member as any).last_login_at
        ? new Date((member as any).last_login_at)
        : null;

    const isRecentlyInvited = (now.getTime() - createdAt.getTime()) < 24 * 60 * 60 * 1000;
    const neverLoggedIn = !lastLogin;

    if (neverLoggedIn && isRecentlyInvited) {
      return { status: 'pending', label: 'Invitation Pending', icon: Clock, color: 'text-orange-600' };
    }
    if (neverLoggedIn) {
      return { status: 'never-logged-in', label: 'Never Logged In', icon: AlertCircle, color: 'text-red-600' };
    }
    return null;
  };

  const filteredMembers = membersWithPresence.filter((member) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      !q
      || member.name.toLowerCase().includes(q)
      || member.email.toLowerCase().includes(q);
    if (!matchesSearch) return false;

    const role = (member.role || '').toString().toUpperCase();
    if (roleFilter.length > 0 && !roleFilter.includes(role)) return false;

    const invitationStatus = getInvitationStatus(member);
    const statusKey = teamMemberStatusKey(member, invitationStatus);
    if (statusFilter.length > 0 && !statusFilter.includes(statusKey)) return false;

    return true;
  });

  const pendingInviteIds = useMemo(() => {
    const ids = new Set<string>();
    for (const member of teamMembers) {
      const invitationStatus = getInvitationStatus(member);
      if (invitationStatus?.status === 'pending' || invitationStatus?.status === 'never-logged-in') {
        ids.add(member.id);
      }
    }
    return ids;
  }, [teamMembers]);

  const teamChannelName = currentUser?.company
    ? `${currentUser.company}${/team$/i.test(currentUser.company) ? '' : ' team'}`
    : 'Team chat';

  const seatLimit = teamStats?.seat_limit ?? entitlements?.entitlements?.limits?.users;
  const seatsUsed = teamStats
    ? teamStats.active_members + 1
    : membersWithPresence.filter((m) => m.is_active).length;
  const seatUsageLabel =
    typeof seatLimit === 'number' && seatLimit > 0
      ? `${seatsUsed} of ${seatLimit} members active`
      : `${seatsUsed} member${seatsUsed === 1 ? '' : 's'} active`;
  const seatLimitReached =
    typeof seatLimit === 'number' && seatLimit > 0 && seatsUsed >= seatLimit;

  const roleLimits = teamStats?.role_limits;
  const roleUsage = teamStats?.role_usage;
  const fmtCap = (cap: number | null | undefined) =>
    cap === -1 ? '∞' : typeof cap === 'number' ? String(cap) : '—';
  const perRoleSummary =
    roleLimits && roleUsage &&
    [roleLimits.admin, roleLimits.user, roleLimits.agent].some((c) => typeof c === 'number')
      ? `Admins ${roleUsage.admin}/${fmtCap(roleLimits.admin)} · Users ${roleUsage.user}/${fmtCap(roleLimits.user)} · Agents ${roleUsage.agent}/${fmtCap(roleLimits.agent)}`
      : null;

  const getAssignedBusinessAccounts = (member: TeamMember) => {
    if (member.assigned_accounts && member.assigned_accounts.length > 0) {
      return member.assigned_accounts;
    }

    if (member.is_organization_owner || member.is_super_admin || (member.role || '').toString().toUpperCase() === 'ADMIN') {
      return (businessAccounts || []).map(ba => ({
        id: ba.id,
        name: ba.name,
        permissions: { has_all_conversations_access: true } // Owners usually have full access
      }));
    }

    return [];
  };

  const handleEditUser = (user: TeamMember) => {
    setEditingUser(user);
  };



  const handleInviteMember = async (userData: any) => {
    try {
      const response = await TeamService.inviteMember(userData);

      if (response.success) {
        await loadTeamData(); // Reload data
        setIsInviteDialogOpen(false);
        const password = (response.data as any)?.password;
        toast({
          title: "Success",
          description: password
            ? `Team member created successfully! Password: ${password}`
            : "Team member created successfully!",
          variant: "success",
          duration: password ? 10000 : 5000, // Show longer if password is displayed
        });
      } else {
        showApiError(response, { fallback: { title: "Couldn't invite team member", description: "Failed to invite team member." } });
      }
    } catch (err: any) {
      showApiError(err, { fallback: { title: "Couldn't invite team member", description: "Failed to invite team member." } });
    }
  };

  const handleUpdateMember = async (updatedUser: any) => {
    try {
      const previousRole = (editingUser?.role || '').toString().trim().toUpperCase();
      const newRole = (updatedUser?.role || '').toString().trim().toUpperCase();
      const editedMember = editingUser;

      const payload = {
        ...updatedUser,
        hasAllConversationsAccess:
          updatedUser.hasAllConversationsAccess ?? updatedUser.has_all_conversations_access,
      };
      const response = await TeamService.updateMember(updatedUser.id, payload);
      if (response.success) {
        await loadTeamData(); // Reload data
        setEditingUser(null);

        const roleChanged = !!previousRole && !!newRole && previousRole !== newRole;
        const roleChangedToNonAdmin = roleChanged && newRole !== 'ADMIN';
        const roleChangedToAdmin = roleChanged && newRole === 'ADMIN';

        if (roleChangedToNonAdmin) {
          const roleLabel = newRole === 'AGENT' ? 'Agent' : newRole === 'USER' ? 'User' : newRole;
          toast({
            title: `Role changed to ${roleLabel}`,
            description: `Choose which sidebar sections this ${roleLabel.toLowerCase()} can access.`,
            variant: "success",
          });
        } else if (roleChangedToAdmin) {
          toast({
            title: "Team member updated",
            description: "Role changed to Admin — full access granted.",
            variant: "success",
          });
        } else {
          toast({
            title: "Success",
            description: "Team member updated successfully",
            variant: "success",
          });
        }

        if (roleChangedToNonAdmin) {
          setPermissionsUser({
            ...(editedMember || {}),
            ...updatedUser,
            role: newRole,
            menu_permissions: null,
            preferences: null,
          } as any);
        }
      } else {
        showApiError(response, { fallback: { title: "Couldn't update team member", description: "Failed to update team member." } });
      }
    } catch (err: any) {
      showApiError(err, { fallback: { title: "Couldn't update team member", description: "Failed to update team member." } });
    }
  };

  const handleChangePassword = async (userId: string, newPassword: string) => {
    try {
      const response = await TeamService.changeUserPassword(userId, newPassword);
      if (response.success) {
        toast({
          title: "Success",
          description: "Password changed successfully",
          variant: "success",
        });
      } else {
        showApiError(response, { fallback: { title: "Couldn't change password", description: "Failed to change password." } });
      }
    } catch (err) {
      showApiError(err, { fallback: { title: "Couldn't change password", description: "Failed to change password." } });
      throw err; // Re-throw to handle in dialog
    }
  };

  const handlePasswordChangeClick = (user: TeamMember) => {
    setPasswordChangeUser(user);
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordChangeConfirm = async (newPassword: string) => {
    if (!passwordChangeUser) return;

    try {
      setPasswordLoading(true);
      await handleChangePassword(passwordChangeUser.id, newPassword);
      setIsPasswordDialogOpen(false);
      setPasswordChangeUser(null);
    } catch (error) {
    } finally {
      setPasswordLoading(false);
    }
  };

  const extractMenuPerms = (m: any): string[] | null => {
    if (!m) return null;
    let prefs = m.preferences;
    if (typeof prefs === 'string') {
      try { prefs = JSON.parse(prefs); } catch { prefs = null; }
    }
    const mp = prefs?.menu_permissions ?? m.menu_permissions ?? null;
    return Array.isArray(mp) ? mp : null;
  };

  const handleSavePermissions = async (memberId: string, menuPermissions: string[] | null) => {
    try {
      const memberResponse = await TeamService.getTeamMembers();
      if (memberResponse.success && Array.isArray(memberResponse.data)) {
        const latestMember = memberResponse.data.find(m => m.id === memberId);
        if (latestMember) {
          const latestPerms = extractMenuPerms(latestMember);
          const currentPerms = extractMenuPerms(permissionsUser);

          const norm = (v: string[] | null) => (v === null ? null : [...v].sort());
          if (JSON.stringify(norm(latestPerms)) !== JSON.stringify(norm(currentPerms))) {
            toast({
              title: 'Conflict Detected',
              description: 'Permissions were changed elsewhere. Reloading...',
              variant: 'destructive',
            });
            await loadTeamData();
            return;
          }
        }
      }

      const response = await TeamService.updateMember(memberId, { menuPermissions });
      if (response.success) {
        await loadTeamData();
        toast({ variant: "success", title: 'Success', description: 'Permissions updated successfully' });
      } else {
        showApiError(response, { fallback: { title: "Couldn't update permissions", description: 'Failed to update permissions.' } });
      }
    } catch (error: any) {
      console.error('Error saving permissions:', error);
      showApiError(error, { fallback: { title: "Couldn't update permissions", description: 'Failed to save permissions.' } });
    }
  };

  const handlePasswordDialogClose = (open: boolean) => {
    setIsPasswordDialogOpen(open);
    if (!open) {
      setPasswordChangeUser(null);
      setPasswordLoading(false);
    }
  };

  const roundRobin = useRoundRobinWorkspace(toast, activeManagementTab === 'round-robin');

  const membersFilterBar = activeManagementTab === 'members' ? (
    <TeamMembersFilterBar
      search={searchTerm}
      onSearchChange={setSearchTerm}
      roleSelected={roleFilter}
      onRoleChange={setRoleFilter}
      statusSelected={statusFilter}
      onStatusChange={setStatusFilter}
      onInvite={isAdmin ? () => setIsInviteDialogOpen(true) : undefined}
      inviteDisabled={seatLimitReached}
      inviteHint={
        seatLimitReached
          ? `You've reached your plan limit (${seatLimit} members). Deactivate a member or upgrade the plan to add more.`
          : null
      }
    />
  ) : undefined;

  const roundRobinFilterBar = activeManagementTab === 'round-robin' ? (
    <TeamRoundRobinFilterBar
      search={roundRobin.search}
      onSearchChange={roundRobin.setSearch}
      rotationSelected={roundRobin.rotationFilter}
      onRotationChange={roundRobin.setRotationFilter}
      statusSelected={roundRobin.statusFilter}
      onStatusChange={roundRobin.setStatusFilter}
      autoAssignEnabled={roundRobin.autoAssignEnabled}
      autoAssignLoading={roundRobin.settingsLoading}
      autoAssignPending={roundRobin.updateSettingsMutation.isPending}
      onAutoAssignChange={(checked) => roundRobin.updateSettingsMutation.mutate(checked)}
    />
  ) : undefined;

  return (
    <>
    <TeamManagementShell
      tab={activeManagementTab}
      onTabChange={setActiveManagementTab}
      memberCount={teamMembers.length}
      filterBar={membersFilterBar ?? roundRobinFilterBar}
    >
      {activeManagementTab === 'members' && (
        <>
          {loading ? (
            <TeamMembersTableSkeleton />
          ) : error ? (
            <div className="px-[15px] py-10 text-center">
              <p className="mb-4 text-[13px] text-[#B42318]">{error}</p>
              <Button size="sm" onClick={loadTeamData}>Retry</Button>
            </div>
          ) : (
            <div className="px-[15px] py-4">
              <div className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[920px] table-fixed border-collapse text-[13px]">
                    <colgroup>
                      <col style={{ width: '24%' }} />
                      <col style={{ width: '24%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '16%' }} />
                      <col style={{ width: '4%' }} />
                    </colgroup>
                    <thead className="border-b border-[#EAECF0] bg-[#FAFAFB]">
                      <tr>
                        <th className={thClass}>Name</th>
                        <th className={thClass}>Email</th>
                        <th className={thClass}>Role</th>
                        <th className={thClass}>Business access</th>
                        <th className={thClass}>Status</th>
                        <th className={`${thClass} w-10 px-2`} aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMembers.map((member) => {
                        const invitationStatus = getInvitationStatus(member);
                        const statusKey = teamMemberStatusKey(member, invitationStatus);
                        const statusPill = teamStatusPill(statusKey);
                        const rolePill = teamRolePill(member);
                        const isSelf = !!(currentUser && member.email === currentUser.email);
                        const isOwnerRow = !!member.is_organization_owner;
                        const lockActions = isOwnerRow || isSelf;
                        const accessLabel = formatBusinessAccessLabel(
                          member,
                          getAssignedBusinessAccounts(member),
                          businessAccounts?.length ?? 0,
                        );

                        return (
                          <tr
                            key={member.id}
                            className="border-b border-[#F2F4F7] last:border-b-0 hover:bg-[#F9FAFB]"
                          >
                            <td className={tdClass}>
                              <div className="flex min-w-0 items-center gap-2.5">
                                <Avatar className="h-8 w-8 shrink-0">
                                  <AvatarFallback className="bg-[#F2F4F7] text-[11px] font-semibold text-[#475467]">
                                    {teamMemberInitials(member.name || member.email || '?')}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0">
                                  <p className="truncate font-semibold text-[#101828]">
                                    {member.name}
                                    {isSelf ? (
                                      <span className="ml-1 font-normal text-[#98A2B3]">(you)</span>
                                    ) : null}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className={cn(tdClass, 'truncate text-[#475467]')}>{member.email}</td>
                            <td className={tdClass}>
                              <SoftRolePill {...rolePill} />
                            </td>
                            <td className={cn(tdClass, 'truncate text-[#475467]')} title={accessLabel}>
                              {accessLabel}
                            </td>
                            <td className={tdClass}>
                              <DotStatusPill {...statusPill} />
                            </td>
                            <td className={`${tdClass} px-2`}>
                              {lockActions ? (
                                <span
                                  className="inline-flex items-center gap-1 text-[11px] text-[#98A2B3]"
                                  title={isOwnerRow
                                    ? "The organization owner can't be edited, deactivated, or removed."
                                    : 'This is your own account.'}
                                >
                                  <Lock className="h-3 w-3" />
                                </span>
                              ) : isAdmin ? (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#98A2B3] hover:bg-[#F2F4F7] hover:text-[#101828]"
                                      aria-label="Member actions"
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuItem onClick={() => handleEditUser(member)}>
                                      <Edit className="mr-2 h-3.5 w-3.5" />
                                      Edit member
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setPermissionsUser(member)}>
                                      <ShieldCheck className="mr-2 h-3.5 w-3.5" />
                                      Menu permissions
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handlePasswordChangeClick(member)}>
                                      <Key className="mr-2 h-3.5 w-3.5" />
                                      Change password
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              ) : (
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600"
                                  onClick={() => toast({
                                    title: 'Permission Denied',
                                    description: 'Only admins can manage team members. Please contact your admin.',
                                    variant: 'warning',
                                  })}
                                >
                                  <ShieldAlert className="h-3 w-3" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {filteredMembers.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <Users className="mx-auto mb-3 h-10 w-10 text-[#98A2B3]" />
                    <h3 className="mb-1 text-[14px] font-semibold text-[#101828]">No members found</h3>
                    <p className="mb-4 text-[13px] text-[#667085]">
                      {searchTerm || roleFilter.length || statusFilter.length
                        ? 'Try adjusting your search or filters'
                        : 'Get started by inviting your first teammate'}
                    </p>
                    {!searchTerm && roleFilter.length === 0 && statusFilter.length === 0 && isAdmin && !seatLimitReached ? (
                      <button
                        type="button"
                        onClick={() => setIsInviteDialogOpen(true)}
                        className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-brand px-3 text-[13px] font-semibold text-white hover:bg-brand-hover"
                      >
                        Invite teammate
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <div className="border-t border-[#EAECF0] px-3.5 py-2.5 text-[12px] text-[#98A2B3]">
                    Showing {filteredMembers.length} of {teamMembers.length} members
                    {seatUsageLabel ? ` · ${seatUsageLabel}` : ''}
                    {perRoleSummary ? ` · ${perRoleSummary}` : ''}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {activeManagementTab === 'messaging' && (
        <div className="px-[15px] py-4">
          <div
            style={{ height: 'calc(100vh - 180px)' }}
            className="flex min-h-[420px] overflow-hidden rounded-2xl border border-[#EAECF0] bg-white"
          >
            <div className="w-[280px] shrink-0 border-r border-[#EAECF0] sm:w-[300px]">
              <TeamMessagingSidebar
                pane={messagingTab === 'team' ? 'team' : 'dm'}
                teamRoomId={orgTeamRoomId}
                teamChannelName={teamChannelName}
                selectedMemberId={messagingSelectedMember}
                pendingInviteIds={pendingInviteIds}
                onSelectTeam={handleMessagingSelectTeam}
                onSelectMember={handleMessagingMemberSelect}
              />
            </div>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              {messagingTab === 'team' ? (
                <ChatWindow
                  roomId={orgTeamRoomId}
                  roomName={teamChannelName}
                  variant="cardTheme"
                  headerSubtitle={`${Math.max(teamMembers.length, 1)} members · Use @ to mention`}
                  composerPlaceholder="Message the team... use @ to mention"
                  className="h-full rounded-none border-0"
                />
              ) : messagingSelectedMember ? (
                <DirectMessageWindow
                  recipientId={messagingSelectedMember}
                  recipientName={messagingSelectedMemberName}
                  onClose={handleMessagingSelectTeam}
                  variant="cardTheme"
                  className="h-full rounded-none border-0"
                />
              ) : (
                <div className="flex h-full items-center justify-center bg-[#F9FAFB]">
                  <div className="max-w-md px-6 text-center">
                    <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#F2F4F7]">
                      <MessageCircle className="h-8 w-8 text-[#98A2B3]" />
                    </div>
                    <h3 className="mb-2 text-[15px] font-semibold text-[#101828]">Select a conversation</h3>
                    <p className="text-[13px] leading-relaxed text-[#667085]">
                      Pick the team channel or a teammate on the left to start chatting.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <UserSearchModal
            isOpen={isUserSearchOpen}
            onClose={() => setIsUserSearchOpen(false)}
            onStartDirectMessage={handleMessagingStartDM}
          />
        </div>
      )}

      {activeManagementTab === 'round-robin' && (
        <div className="px-[15px] py-4">
          <RoundRobinPanel {...roundRobin} />
        </div>
      )}
    </TeamManagementShell>

      <MinimalInviteDialog
        open={isInviteDialogOpen}
        onOpenChange={setIsInviteDialogOpen}
        onInvite={handleInviteMember}
      />

      {editingUser && (
        <MinimalEditDialog
          open={!!editingUser}
          onOpenChange={() => setEditingUser(null)}
          user={editingUser}
          onSave={handleUpdateMember}
        />
      )}



      <ChangePasswordDialog
        open={isPasswordDialogOpen}
        onOpenChange={handlePasswordDialogClose}
        onConfirm={handlePasswordChangeConfirm}
        memberName={passwordChangeUser?.name || ''}
        memberEmail={passwordChangeUser?.email || ''}
        loading={passwordLoading}
      />

      {permissionsUser && (
        <MemberPermissionsDialog
          open={!!permissionsUser}
          onOpenChange={(open) => { if (!open) setPermissionsUser(null); }}
          member={permissionsUser}
          onSave={handleSavePermissions}
        />
      )}
    </>
  );
};

function useRoundRobinWorkspace(toast: ReturnType<typeof useToast>['toast'], enabled: boolean) {
  const queryClient = useQueryClient();
  const { selectedWorkspaceId } = useWorkspaceContext();
  const [search, setSearch] = useState('');
  const [rotationFilter, setRotationFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  const { data: settingsRes, isLoading: settingsLoading } = useQuery({
    queryKey: ['round-robin-settings', selectedWorkspaceId],
    queryFn: () => roundRobinApi.getSettings(),
    enabled,
  });

  const { data: agentsRes, isLoading: agentsLoading } = useQuery({
    queryKey: ['round-robin-agents', selectedWorkspaceId],
    queryFn: () => roundRobinApi.getAgents(),
    enabled,
  });

  const settings = settingsRes?.data;
  const agents = agentsRes?.data ?? [];
  const autoAssignEnabled = settings?.is_enabled ?? false;

  const updateSettingsMutation = useMutation({
    mutationFn: (is_enabled: boolean) => roundRobinApi.updateSettings({ is_enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['round-robin-settings'] });
      toast({ variant: "success", title: 'Round robin setting saved' });
    },
    onError: (error) => showApiError(error, { fallback: { title: 'Failed to update setting' } }),
  });

  const updateAgentMutation = useMutation({
    mutationFn: ({ userId, enabled: agentEnabled }: { userId: string; enabled: boolean }) =>
      roundRobinApi.updateAgent(userId, agentEnabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['round-robin-agents'] }),
    onError: (error) => showApiError(error, { fallback: { title: 'Failed to update agent' } }),
  });

  const filteredAgents = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rotation = rotationFilter[0];
    const status = statusFilter[0];
    return agents.filter((agent) => {
      if (q && !(agent.name || '').toLowerCase().includes(q)) return false;
      const inRotation = agent.round_robin_enabled === true;
      if (rotation === 'in' && !inRotation) return false;
      if (rotation === 'out' && inRotation) return false;
      const isInactive = agent.is_active === false;
      if (status === 'active' && isInactive) return false;
      if (status === 'inactive' && !isInactive) return false;
      return true;
    });
  }, [agents, search, rotationFilter, statusFilter]);

  const inRotationCount = agents.filter((agent) => agent.round_robin_enabled === true).length;

  return {
    search,
    setSearch,
    rotationFilter,
    setRotationFilter,
    statusFilter,
    setStatusFilter,
    settingsLoading,
    agentsLoading,
    agents,
    autoAssignEnabled,
    updateSettingsMutation,
    updateAgentMutation,
    filteredAgents,
    inRotationCount,
  };
}

type RoundRobinPanelProps = ReturnType<typeof useRoundRobinWorkspace>;

const RoundRobinPanel: React.FC<RoundRobinPanelProps> = ({
  search,
  rotationFilter,
  statusFilter,
  settingsLoading,
  agentsLoading,
  agents,
  autoAssignEnabled,
  updateAgentMutation,
  filteredAgents,
  inRotationCount,
}) => {
  return (
        <div className="overflow-hidden rounded-2xl border border-[#EAECF0] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
          {!autoAssignEnabled && !settingsLoading ? (
            <div className="border-b border-[#EAECF0] bg-[#F9FAFB] px-3.5 py-2.5 text-[12px] text-[#667085]">
              Auto-assign is off — new WhatsApp chats won&apos;t rotate until you turn it on.
              You can still choose who is in the pool.
            </div>
          ) : null}

          {agentsLoading ? (
            <div className="space-y-3 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="ml-auto h-5 w-16 rounded-full" />
                  <Skeleton className="h-6 w-11 rounded-full" />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] table-fixed border-collapse text-[13px]">
                  <colgroup>
                    <col style={{ width: '36%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '24%' }} />
                  </colgroup>
                  <thead className="border-b border-[#EAECF0] bg-[#FAFAFB]">
                    <tr>
                      <th className={thClass}>Agent</th>
                      <th className={thClass}>Role</th>
                      <th className={thClass}>Status</th>
                      <th className={`${thClass} text-right`}>In rotation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAgents.map((agent) => {
                      const isInactive = agent.is_active === false;
                      const statusPill = teamStatusPill(isInactive ? 'inactive' : 'active');
                      const rolePill = teamRolePillFromRole(agent.role);
                      const switchEl = (
                        <Switch
                          checked={agent.round_robin_enabled ?? false}
                          onCheckedChange={(checked) =>
                            updateAgentMutation.mutate({ userId: agent.user_id, enabled: checked })
                          }
                          disabled={updateAgentMutation.isPending || isInactive}
                          aria-label={`Include ${agent.name || 'agent'} in rotation`}
                        />
                      );
                      return (
                        <tr
                          key={agent.user_id || agent.id}
                          className="border-b border-[#F2F4F7] last:border-b-0 hover:bg-[#F9FAFB]"
                        >
                          <td className={tdClass}>
                            <div className="flex min-w-0 items-center gap-2.5">
                              <div className="relative shrink-0">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="bg-[#F2F4F7] text-[11px] font-semibold text-[#475467]">
                                    {teamMemberInitials(agent.name || '?')}
                                  </AvatarFallback>
                                </Avatar>
                                <span
                                  className={cn(
                                    'absolute bottom-0 right-0 h-2 w-2 rounded-full border-2 border-white',
                                    agent.is_online ? 'bg-[#12B76A]' : 'bg-[#D0D5DD]',
                                  )}
                                  aria-hidden
                                />
                              </div>
                              <p className="truncate font-semibold text-[#101828]">{agent.name}</p>
                            </div>
                          </td>
                          <td className={tdClass}>
                            <SoftRolePill {...rolePill} />
                          </td>
                          <td className={tdClass}>
                            <DotStatusPill {...statusPill} />
                          </td>
                          <td className={`${tdClass} text-right`}>
                            {isInactive ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex" tabIndex={0}>
                                    {switchEl}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                  This member is inactive — reactivate them to enable round-robin.
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              switchEl
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredAgents.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <Users className="mx-auto mb-3 h-10 w-10 text-[#98A2B3]" />
                  <h3 className="mb-1 text-[14px] font-semibold text-[#101828]">No agents found</h3>
                  <p className="text-[13px] text-[#667085]">
                    {search || rotationFilter.length || statusFilter.length
                      ? 'Try adjusting your search or filters'
                      : 'Invite teammates from the Members tab to add them to rotation'}
                  </p>
                </div>
              ) : (
                <div className="border-t border-[#EAECF0] px-3.5 py-2.5 text-[12px] text-[#98A2B3]">
                  Showing {filteredAgents.length} of {agents.length} agents
                  {` · ${inRotationCount} in rotation`}
                </div>
              )}
            </>
          )}
        </div>
  );
};
